import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ActionIcon, Badge, Button, FileInput, Group, Image, LoadingOverlay, Modal, NumberInput, Paper, Select, Stack, Switch, Table, Text, TextInput, Title } from "@mantine/core";
import { IconArrowDown, IconArrowUp, IconEye, IconEyeOff, IconPencil, IconPlus, IconTrash } from "@tabler/icons-react";
import { useNotification } from "../adminProjectNotifications/popupnotifications";
import { API_BASE_URL } from "../../config/baseUrls";

// Sponsors (GET/POST /admin/sponsors, PATCH /admin/sponsors/:id, PATCH /admin/sponsors/reorder, DELETE /admin/sponsors/:id).
// These are the partner logos in the "Powered by" section of otatokyo.jp. Changes show on the site within about 5 minutes.
// Logos: pick any PNG/JPG/WebP. The page trims empty margins and shrinks big files before uploading.

const MAX_LOGO_EDGE = 900;
const EMPTY = { name: "", tier: "Tsuki", logoUrl: "", linkUrl: "", logoHeight: "", active: true };

const headers = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
});

const call = async (path, options = {}) => {
  const res = await fetch(`${API_BASE_URL}${path}`, { ...options, headers: headers() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) throw new Error(data.error || data.message || "Something went wrong.");
  return data;
};

// Trim transparent / white margins, cap the long edge, keep transparency (PNG).
export const prepareLogo = async (file) => {
  const bitmap = await createImageBitmap(file);
  const c = document.createElement("canvas");
  c.width = bitmap.width;
  c.height = bitmap.height;
  const ctx = c.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();
  const { data, width, height } = ctx.getImageData(0, 0, c.width, c.height);
  let minX = width, minY = height, maxX = -1, maxY = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const i = (y * width + x) * 4;
      const visible = data[i + 3] > 12 && !(data[i] > 245 && data[i + 1] > 245 && data[i + 2] > 245);
      if (visible) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) {
    minX = 0; minY = 0; maxX = width - 1; maxY = height - 1;
  }
  const w = maxX - minX + 1;
  const h = maxY - minY + 1;
  const scale = Math.min(1, MAX_LOGO_EDGE / Math.max(w, h));
  const out = document.createElement("canvas");
  out.width = Math.max(1, Math.round(w * scale));
  out.height = Math.max(1, Math.round(h * scale));
  out.getContext("2d").drawImage(c, minX, minY, w, h, 0, 0, out.width, out.height);
  return new Promise((resolve, reject) => out.toBlob((b) => (b ? resolve(b) : reject(new Error("Could not read that image."))), "image/png"));
};

const uploadLogo = async (blob, name) => {
  const signed = await fetch(`${API_BASE_URL}/auth/upload-url`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ name: `sponsor_${name}`, contentType: "image/png", size: blob.size }),
  });
  const info = await signed.json().catch(() => ({}));
  if (!signed.ok || !info.uploadUrl) throw new Error(info.error || "Could not start the upload.");
  const put = await fetch(info.uploadUrl, { method: "PUT", headers: info.headers || { "Content-Type": "image/png" }, body: blob });
  if (!put.ok) throw new Error("Upload failed. Please try again.");
  return info.fileUrl;
};

export default function SponsorsPage() {
  const [rows, setRows] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState(null); // null | { id?: string, ...fields }
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const { showError, showSuccess } = useNotification();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await call("/admin/sponsors");
      setRows(d.data);
      setTiers(d.tiers);
    } catch (e) {
      showError("Error", e.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const grouped = useMemo(() => tiers.map((t) => ({ tier: t, items: rows.filter((r) => r.tier === t) })), [rows, tiers]);

  const move = async (tier, index, dir) => {
    const items = rows.filter((r) => r.tier === tier);
    const j = index + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[index], next[j]] = [next[j], next[index]];
    const others = rows.filter((r) => r.tier !== tier);
    setRows([...others, ...next.map((r, i) => ({ ...r, sortOrder: i }))]);
    try {
      await call("/admin/sponsors/reorder", { method: "PATCH", body: JSON.stringify({ ids: next.map((r) => r.id) }) });
    } catch (e) {
      showError("Error", e.message);
      load();
    }
  };

  const toggle = async (r) => {
    setBusy(true);
    try {
      await call(`/admin/sponsors/${r.id}`, { method: "PATCH", body: JSON.stringify({ active: !r.active }) });
      showSuccess("Saved", r.active ? `${r.name} is now hidden.` : `${r.name} is now shown.`);
      await load();
    } catch (e) {
      showError("Error", e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${r.name}? This removes it from the site. (Use Hide to keep it for later.)`)) return;
    setBusy(true);
    try {
      await call(`/admin/sponsors/${r.id}`, { method: "DELETE" });
      showSuccess("Deleted", `${r.name} removed.`);
      await load();
    } catch (e) {
      showError("Error", e.message);
    } finally {
      setBusy(false);
    }
  };

  const pickFile = async (file) => {
    if (!file) return;
    try {
      setSaving(true);
      const blob = await prepareLogo(file);
      const url = await uploadLogo(blob, (edit.name || file.name || "logo").replace(/[^a-zA-Z0-9]/g, "_").slice(0, 30));
      setEdit((p) => ({ ...p, logoUrl: url }));
    } catch (e) {
      showError("Error", e.message || "Could not use that image.");
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        name: edit.name,
        tier: edit.tier,
        logoUrl: edit.logoUrl,
        linkUrl: edit.linkUrl || null,
        logoHeight: edit.logoHeight === "" || edit.logoHeight == null ? null : Number(edit.logoHeight),
        active: edit.active,
      };
      if (edit.id) await call(`/admin/sponsors/${edit.id}`, { method: "PATCH", body: JSON.stringify(body) });
      else await call("/admin/sponsors", { method: "POST", body: JSON.stringify(body) });
      showSuccess("Saved", "Live on the site within about 5 minutes.");
      setEdit(null);
      await load();
    } catch (e) {
      showError("Error", e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack p="md" pos="relative">
      <LoadingOverlay visible={loading || busy} />
      <Group justify="space-between">
        <div>
          <Title order={2}>Sponsors</Title>
          <Text c="dimmed" size="sm">Partner logos in the "Powered by" section of otatokyo.jp. Changes show on the site within about 5 minutes.</Text>
        </div>
        <Button leftSection={<IconPlus size={16} />} onClick={() => setEdit({ ...EMPTY })}>Add sponsor</Button>
      </Group>

      {grouped.map(({ tier, items }) => (
        <Paper key={tier} withBorder p="md" radius="md">
          <Group justify="space-between" mb="xs">
            <Title order={4}>{tier}</Title>
            <Badge variant="light">{items.length}</Badge>
          </Group>
          {items.length === 0 ? (
            <Text c="dimmed" size="sm">No sponsors in this tier.</Text>
          ) : (
            <Table verticalSpacing="xs">
              <Table.Tbody>
                {items.map((r, i) => (
                  <Table.Tr key={r.id} style={{ opacity: r.active ? 1 : 0.5 }}>
                    <Table.Td w={140}><Image src={r.logoUrl} alt={r.name} h={44} w="auto" fit="contain" /></Table.Td>
                    <Table.Td>
                      <Text fw={600}>{r.name}</Text>
                      {!r.active && <Badge color="gray" size="xs">Hidden</Badge>}
                    </Table.Td>
                    <Table.Td w={260}>
                      <Group gap={4} justify="flex-end">
                        <ActionIcon variant="subtle" aria-label="Move up" disabled={i === 0} onClick={() => move(tier, i, -1)}><IconArrowUp size={16} /></ActionIcon>
                        <ActionIcon variant="subtle" aria-label="Move down" disabled={i === items.length - 1} onClick={() => move(tier, i, 1)}><IconArrowDown size={16} /></ActionIcon>
                        <ActionIcon variant="subtle" aria-label={r.active ? "Hide" : "Show"} onClick={() => toggle(r)}>{r.active ? <IconEye size={16} /> : <IconEyeOff size={16} />}</ActionIcon>
                        <ActionIcon variant="subtle" aria-label="Edit" onClick={() => setEdit({ ...r, linkUrl: r.linkUrl || "", logoHeight: r.logoHeight ?? "" })}><IconPencil size={16} /></ActionIcon>
                        <ActionIcon variant="subtle" color="red" aria-label="Delete" onClick={() => remove(r)}><IconTrash size={16} /></ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Paper>
      ))}

      <Modal opened={!!edit} onClose={() => !saving && setEdit(null)} title={edit?.id ? "Edit sponsor" : "Add sponsor"} centered>
        {edit && (
          <Stack>
            <TextInput label="Name" value={edit.name} maxLength={80} onChange={(e) => setEdit({ ...edit, name: e.currentTarget.value })} required />
            <Select label="Tier" data={tiers} value={edit.tier} onChange={(v) => v && setEdit({ ...edit, tier: v })} allowDeselect={false} required />
            <FileInput label="Logo" description="PNG, JPG or WebP. Empty margins are trimmed automatically." accept="image/png,image/jpeg,image/webp" onChange={pickFile} placeholder={edit.logoUrl ? "Replace logo" : "Choose logo"} />
            {edit.logoUrl && (
              <Paper withBorder p="sm" bg="white"><Image src={edit.logoUrl} alt="Logo preview" h={70} w="auto" fit="contain" /></Paper>
            )}
            <TextInput label="Website link (optional)" placeholder="https://" value={edit.linkUrl} onChange={(e) => setEdit({ ...edit, linkUrl: e.currentTarget.value })} />
            <NumberInput label="Logo height (optional)" description="Leave empty for the default. Raise it for tall or round logos that look too small (30 to 140)." min={30} max={140} value={edit.logoHeight} onChange={(v) => setEdit({ ...edit, logoHeight: v })} />
            <Switch label="Show on the site" checked={edit.active} onChange={(e) => setEdit({ ...edit, active: e.currentTarget.checked })} />
            <Group justify="flex-end">
              <Button variant="default" disabled={saving} onClick={() => setEdit(null)}>Cancel</Button>
              <Button loading={saving} disabled={!edit.name.trim() || !edit.logoUrl} onClick={save}>Save</Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
